import Immutable from 'immutable';
import type { AudioSourceData, MediaSourceData } from '@lumen5/luminary';

import type {
    AssetContainerModel,
    AssetGenerationData,
    AssetGenerationResponse,
    AudioAssetModel,
    AudioAssetResponse,
    IconAssetModel,
    ImageAssetModel,
    MediaFolder,
    MediaSearchResponse,
    SlideBackgroundModel,
    VideoAssetModel,
} from 'js/src/client';
import {
    AssetContainerTypes,
    AssetTypes,
    AudioSearchSourceEnum,
    BackgroundVideoDurationSeconds,
    FiltersEnum,
    MediaSearchSourceEnum,
    SlideBackgroundModelTypeEnum,
    SortEnum,
} from 'js/src/client';
import { EDITOR_TAB_OPTIONS } from 'js/src/data/app/app-types';
import type { media_reducers_map } from 'js/src/data/media/media-reducers';
import type {
    BackgroundAssetContainerModel,
    IconAssetContainerModel,
} from 'js/src/data/uploads/uploads-types';
import {
    ImmutableFromJS,
    type ImmutableMap,
    ImmutableToJS,
    isUserLoggedIn,
    type ValueOf,
} from 'js/src/libs/consts';
import Flags from 'js/src/libs/flags';
import type { OmitReadonly } from 'js/src/libs/OmitReadonly';
import type { DAMConfig } from 'js/src/typings/global';

export enum MEDIA_ACTIONS {
    SET_FILTER = 'SET_FILTER',
    SET_SORT = 'SET_SORT',
    CHANGE_MEDIA_SEARCH_TERM = 'CHANGE_MEDIA_SEARCH_TERM',
    CHANGE_CORRECTED_MEDIA_SEARCH_TERM = 'CHANGE_CORRECTED_MEDIA_SEARCH_TERM',
    CHANGE_SUGGESTED_MEDIA_SEARCH_TERM = 'CHANGE_SUGGESTED_MEDIA_SEARCH_TERM',
    CHANGE_RELATED_MEDIA_SEARCH_TERMS = 'CHANGE_RELATED_MEDIA_SEARCH_TERMS',
    STORE_FREQUENTLY_USED_TAGS = 'STORE_FREQUENTLY_USED_TAGS',
    CHANGE_ACTIVE_SEARCH_CONTEXT = 'CHANGE_ACTIVE_SEARCH_CONTEXT',
    RECEIVE_MEDIA_RESPONSE = 'RECEIVE_MEDIA_RESPONSE',
    RECEIVE_MEDIA_ERROR = 'RECEIVE_MEDIA_ERROR',
    START_MEDIA_SEARCH = 'START_MEDIA_SEARCH',
    TOGGLE_MEDIA_SOURCE_FILTER = 'TOGGLE_MEDIA_SOURCE_FILTER',
    TOGGLE_MEDIA_LICENSE_FILTER = 'TOGGLE_MEDIA_LICENSE_FILTER',
    SET_SELECTED_MEDIA_TYPE_FILTER = 'SET_SELECTED_MEDIA_TYPE_FILTER',
    TOGGLE_FILTER_BY_USER_LEVEL = 'TOGGLE_FILTER_BY_USER_LEVEL',
    SET_IS_FETCHING_RECOMMENDED_SEARCH_TERMS = 'SET_IS_FETCHING_RECOMMENDED_SEARCH_TERMS',
    SET_RECOMMENDED_SEARCH_TERMS = 'SET_RECOMMENDED_SEARCH_TERMS',
    SET_CURRENT_MEDIA_QUERY_SCENE = 'SET_CURRENT_MEDIA_QUERY_SCENE',
    SET_IS_MEDIA_QUERY_SEARCH_LOADING = 'SET_IS_MEDIA_QUERY_SEARCH_LOADING',
    SET_MEDIA_QUERY_SEARCH_RESULTS = 'SET_MEDIA_QUERY_SEARCH_RESULTS',
    SET_IS_SIMILAR_SEARCH_CONTEXT = 'SET_IS_SIMILAR_SEARCH_CONTEXT',
    SET_IS_SIMILAR_SEARCH_LOADING = 'SET_IS_SIMILAR_SEARCH_LOADING',
    SET_SIMILAR_SEARCH_RESULTS = 'SET_SIMILAR_SEARCH_RESULTS',
    SET_SIMILAR_SEARCH_ORIGINAL_ASSET = 'SET_SIMILAR_SEARCH_ORIGINAL_ASSET',
    DELETE_MEDIA_RESULT = 'DELETE_MEDIA_RESULT',
    SHOW_MEDIA_QUERY_SUGGESTION_ERROR = 'SHOW_MEDIA_QUERY_SUGGESTION_ERROR',
    SET_ALL_FAVORITES = 'SET_ALL_FAVORITES',
    ADD_FAVORITE_MEDIA = 'ADD_FAVORITE_MEDIA',
    REMOVE_FAVORITE_MEDIA = 'REMOVE_FAVORITE_MEDIA',
    TOGGLE_FAVORITE_MEDIA = 'TOGGLE_FAVORITE_MEDIA',
    SET_CURRENT_PLAYING_AUDIO_URL = 'SET_CURRENT_PLAYING_AUDIO_URL',
    SET_CURRENT_PLAYING_AUDIO_SEEK_PERCENT = 'SET_CURRENT_PLAYING_AUDIO_SEEK_PERCENT',
    SET_CURRENT_PLAYING_AUDIO_DURATION = 'SET_CURRENT_PLAYING_AUDIO_DURATION',
    SET_CURRENT_AUDIO_PLAYING_STATUS = 'SET_CURRENT_AUDIO_PLAYING_STATUS',
    SET_CURRENT_PLAYING_AUDIO_SCRUBBER_SEEK_PERCENT = 'SET_CURRENT_PLAYING_AUDIO_SCRUBBER_SEEK_PERCENT',
    UPDATE_MEDIA_TAGS = 'UPDATE_MEDIA_TAGS',
    SET_ACTIVE_FOLDER = 'SET_ACTIVE_FOLDER',
    STORE_ALL_FOLDERS = 'STORE_ALL_FOLDERS',
    ADD_NEW_FOLDER = 'ADD_NEW_FOLDER',
    EDIT_FOLDER_NAME = 'EDIT_FOLDER_NAME',
    DELETE_FOLDER = 'DELETE_FOLDER',
    ADD_RECENTLY_UPLOADED_ASSET_TO_A_FOLDER = 'ADD_RECENTLY_UPLOADED_ASSET_TO_A_FOLDER',
    MOVE_ASSET_TO_FOLDER = 'MOVE_ASSET_TO_FOLDER',
    ADD_MEDIA_TO_UPLOADS = 'ADD_MEDIA_TO_UPLOADS',
    RESET_FOLDER_SEARCH_STATE = 'RESET_FOLDER_SEARCH_STATE',
    DELETE_AUDIO_SUCCESS = 'DELETE_AUDIO_SUCCESS',
    UPDATE_FOLDER_THUMBNAIL = 'UPDATE_FOLDER_THUMBNAIL',
    SET_MUSIC_VOLUME_OVERRIDE = 'SET_MUSIC_VOLUME_OVERRIDE',
    RECEIVE_DAM_CONNECTION_ERROR = 'RECEIVE_DAM_CONNECTION_ERROR',
    SET_CURRENT_MEDIA_GENERATIONS = 'SET_CURRENT_MEDIA_GENERATIONS',
    ADD_PREVIOUS_MEDIA_GENERATIONS = 'ADD_PREVIOUS_MEDIA_GENERATIONS',
    SET_PREVIOUS_MEDIA_GENERATIONS = 'SET_PREVIOUS_MEDIA_GENERATIONS',
    SET_SAVING_AI_MEDIA = 'SET_SAVING_AI_MEDIA',
    SET_MEDIA_ERRORS = 'SET_MEDIA_ERRORS',
    CLEAR_MEDIA_ERRORS = 'CLEAR_MEDIA_ERRORS',
    SET_AI_SIMILAR_GENERATIONS = 'SET_AI_SIMILAR_GENERATIONS',
    SET_IS_AI_SIMILAR_GENERATIONS_LOADING = 'SET_IS_AI_SIMILAR_GENERATIONS_LOADING',
    SET_AUDIO_DURATION_FILTER = 'SET_AUDIO_DURATION_FILTER',
    SET_AI_MEDIA_MODELS = 'SET_AI_MEDIA_MODELS',
    SET_VIDEO_GENERATION_TASK = 'SET_VIDEO_GENERATION_TASK',
    SET_CHART_GENERATION_ASSET = 'SET_CHART_GENERATION_ASSET',
    SET_IS_INITIALIZING_VIDEO_GENERATION = 'SET_IS_INITIALIZING_VIDEO_GENERATION',
    SET_IS_INITIALIZING_IMAGE_GENERATION = 'SET_IS_INITIALIZING_IMAGE_GENERATION',
    SET_SELECTED_REFERENCE_IMAGE = 'SET_SELECTED_REFERENCE_IMAGE',
    SET_ACTIVE_AI_MEDIA_SUB_TAB = 'SET_ACTIVE_AI_MEDIA_SUB_TAB',
    SET_CAROUSEL_MEDIA_RESULTS = 'SET_CAROUSEL_MEDIA_RESULTS',
    SET_USER_SELECTED_GEN_MEDIA_ASPECT_RATIO = 'SET_USER_SELECTED_GEN_MEDIA_ASPECT_RATIO',
}

export const RECORDING_FOLDER_NAME = 'Recordings';
export const SCREEN_RECORDING_FOLDER_NAME = 'Screen Recordings';
export const SCRIPT_COMPOSER_MEDIA_FOLDER_NAME = 'Script Composer media uploads';
export const AI_REFERENCE_MEDIA_FOLDER_NAME = 'AI Reference Media';

export const MEDIA_STORE_PREFIX = 'media';
export const UPLOADS_FOLDER_ORM_PREFIX = 'uploads-folder';

export enum AI_MEDIA_SUB_TAB_OPTIONS {
    IMAGE = 'image',
    VIDEO = 'video',
    CHARTS = 'charts',
    FAVORITES = 'favorites',
}

export interface AudioDurationRange {
    min: number;
    max: number | null;
}

export enum MEDIA_TYPE_OPTIONS {
    ALL = 'all',
    VIDEOS = FiltersEnum.VIDEOS,
    PHOTOS = FiltersEnum.PHOTOS,
}

export enum MEDIA_SOURCE_OPTIONS {
    BASIC = 'basic',
    PREMIUM = 'premium',
    MARKETPLACE = 'marketplace',
}

export enum MEDIA_LICENSE_OPTIONS {
    ALL = 'all-licenses',
    EDITORIAL = 'editorial',
    STOCK = 'stock',
}

// sort methods for uploaded media
export const DEFAULT_SORT_METHOD = SortEnum.LATEST;
export const SORT_DISPLAY_NAMES: Record<SortEnum, string> = {
    [SortEnum.MOST_RELEVANT]: 'Most relevant',
    [SortEnum.LATEST]: 'Newest',
    [SortEnum.EARLIEST]: 'Oldest',
};

export function toAssetType(value: SlideBackgroundModelTypeEnum): AssetTypes {
    if (Object.values(AssetTypes).includes(value as unknown as AssetTypes)) {
        return value as unknown as AssetTypes;
    }
    throw new Error(`Invalid asset type: ${value}`);
}

export type FlattenedAsset = {
    type: SlideBackgroundModelTypeEnum;
} & (ImageAssetModel | VideoAssetModel);

export type VideoFlattenedAsset = {
    type: SlideBackgroundModelTypeEnum.VIDEO;
} & VideoAssetModel;

export function isAudioAsset(
    media?:
        | VideoAssetModel
        | AssetContainerModel
        | AudioAssetModel
        | AudioSourceData
        | FlattenedAsset
        | null,
): media is AudioAssetModel {
    return (
        !!media &&
        (media as AudioAssetModel).mp3_url !== undefined && // Video assets (and others) do not have mp3_url
        Array.isArray((media as AudioAssetModel).waveform) // AudioAsset.sourceData does not have waveform
    );
}

export function isImmutableAudioAsset(media: unknown): media is ImmutableAudioAsset {
    return (media as ImmutableAudioAsset).get('mp3_url') !== undefined;
}

export function isVideoAsset(
    asset?: VideoAssetModel | AudioAssetModel | AudioSourceData,
): asset is VideoAssetModel {
    return !!asset && (asset as VideoAssetModel).has_audio !== undefined; // only VideoAssets have the has_audio field
}

export type ImmutableSlideBackgroundModel = ImmutableMap<SlideBackgroundModel>;
export type ImmutableAudioAsset = ImmutableMap<AudioAssetModel>;
export type ImmutableVideoAsset = ImmutableMap<VideoAssetModel>;
export type ImmutableImageAsset = ImmutableMap<ImageAssetModel>;
export type ImmutableIconAsset = ImmutableMap<IconAssetModel>;

export type ImageBackgroundModel = SlideBackgroundModel & {
    type: SlideBackgroundModelTypeEnum.IMAGE;
    image: ImageAssetModel;
};
export function slideBackgroundModelIsImageBackgroundModel(
    backgroundAsset: SlideBackgroundModel | IconAssetContainerModel,
): backgroundAsset is ImageBackgroundModel {
    return backgroundAsset.type === SlideBackgroundModelTypeEnum.IMAGE;
}
export function immutableSlideBackgroundModelIsImmutableImageBackgroundModel(
    backgroundAsset: ImmutableSlideBackgroundModel,
): backgroundAsset is ImmutableMap<ImageBackgroundModel> {
    return backgroundAsset.get('type') === SlideBackgroundModelTypeEnum.IMAGE;
}

export type VideoBackgroundModel = SlideBackgroundModel & {
    type: SlideBackgroundModelTypeEnum.VIDEO;
    video: VideoAssetModel;
};
export function slideBackgroundModelIsVideoBackgroundModel(
    backgroundAsset: SlideBackgroundModel | IconAssetContainerModel,
): backgroundAsset is VideoBackgroundModel {
    return backgroundAsset.type === SlideBackgroundModelTypeEnum.VIDEO;
}
export function immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(
    backgroundAsset: ImmutableSlideBackgroundModel,
): backgroundAsset is ImmutableMap<VideoBackgroundModel> {
    return backgroundAsset.get('type') === SlideBackgroundModelTypeEnum.VIDEO;
}

export function getImmutableVideoAssetFromImmutableMediaResult(
    media: ImmutableMap<AssetContainerModel>,
) {
    if (!isImmutableAudioAsset(media)) {
        if (
            (media as ImmutableMap<BackgroundAssetContainerModel>).get('type') ===
            AssetContainerTypes.BACKGROUND
        ) {
            return (media as ImmutableMap<BackgroundAssetContainerModel>)
                .get('background')
                ?.get('video');
        }
    }
}

export const getBackgroundVideoDurationSecondsMax = (): number => {
    return BackgroundVideoDurationSeconds.MAX;
};

export function isMediaSourceData(
    media: null | undefined | MediaSourceData | AudioSourceData,
): media is MediaSourceData {
    return (
        !!media &&
        (media as MediaSourceData).type !== undefined &&
        ((media as MediaSourceData).type !== AssetTypes.IMAGE ||
            (media as MediaSourceData).type !== AssetTypes.VIDEO)
    );
}

// Unfortunately we need to use ts-ignore here because FlattenedAsset uses LicenseEnum (from the python)
// while MediaSourceData defines license as a string
export function isFlattenedAssetMediaSourceDataCompatible(
    flattenedAsset: Partial<FlattenedAsset>,

    // @ts-ignore
): flattenedAsset is MediaSourceData {
    return (
        flattenedAsset.type === SlideBackgroundModelTypeEnum.VIDEO ||
        flattenedAsset.type === SlideBackgroundModelTypeEnum.IMAGE
    );
}

export function flattenedAssetIsVideo(
    flattenedAsset: FlattenedAsset,
): flattenedAsset is VideoFlattenedAsset {
    return flattenedAsset.type === SlideBackgroundModelTypeEnum.VIDEO;
}

// util functions for type-guarding backgroundAsset
export function getVideoAssetFromBackgroundAsset(
    backgroundAsset: ImmutableSlideBackgroundModel,
): ImmutableVideoAsset | null | undefined {
    if (backgroundAsset.get('type') === SlideBackgroundModelTypeEnum.VIDEO) {
        return backgroundAsset.get('video');
    }
}
export function getImageAssetFromBackgroundAsset(
    backgroundAsset: ImmutableSlideBackgroundModel,
): ImmutableImageAsset | null | undefined {
    if (backgroundAsset.get('type') === SlideBackgroundModelTypeEnum.IMAGE) {
        return backgroundAsset.get('image');
    }
}
export function convertBackgroundAssetToFlattenedAsset(
    backgroundAsset: SlideBackgroundModel | OmitReadonly<SlideBackgroundModel>,
): FlattenedAsset {
    return Object.assign({ type: backgroundAsset.type }, backgroundAsset[backgroundAsset.type]);
}

export function convertImageAssetToBackgroundAsset(
    imageAsset: ImageAssetModel,
): SlideBackgroundModel {
    return {
        image: imageAsset,
        video: null,
        history_index_user_ids: null,
        type: SlideBackgroundModelTypeEnum.IMAGE,
    };
}

export function convertVideoAssetToBackgroundAsset(
    videoAsset: VideoAssetModel,
): SlideBackgroundModel {
    return {
        image: null,
        video: videoAsset,
        history_index_user_ids: null,
        type: SlideBackgroundModelTypeEnum.VIDEO,
    };
}

export function isAudioSourceData(sourceData: unknown): sourceData is AudioSourceData {
    if (!sourceData || typeof sourceData !== 'object') {
        return false;
    }
    return (sourceData as AudioSourceData).mp3_url !== undefined;
}

export function isImmutableAudioSourceData(
    sourceData: unknown,
): sourceData is ImmutableMap<AudioSourceData> {
    if (!sourceData || !(sourceData instanceof Immutable.Map)) {
        return false;
    }
    return (sourceData as ImmutableMap<AudioSourceData>).get('mp3_url') !== undefined;
}

export type ImmutableWaveformData = Immutable.List<number>;

// MediaFolder instances are joined with the media_reducers_map to create a Folder data structure with search state
type MediaReducersMap = typeof media_reducers_map;
export type MediaFolderWithContents = {
    [K in keyof MediaReducersMap]: ReturnType<MediaReducersMap[K]>;
} & MediaFolder;

export type SearchResponse = MediaSearchResponse | AudioAssetResponse;

// type guard for MediaSearchResponse – 'premium_results' is only present in MediaSearchResponse
export function isMediaSearchResponse(response: SearchResponse): response is MediaSearchResponse {
    return (response as MediaSearchResponse).premium_results !== undefined;
}

/**
 * @deprecated - IO-2579 - we want to merge the SlideBackgroundModel and AssetContainerModel types, so this should not be required soon
 */
export function convertImmutableSlideBackgroundToImmutableAssetContainer(
    background: ImmutableSlideBackgroundModel,
): ImmutableMap<AssetContainerModel> {
    return ImmutableFromJS({
        type: AssetContainerTypes.BACKGROUND,
        background: background.toJS(),
        audio: null,
        icon: null,
        document: null,
        font: null,
        document_id: null,
        article_id: null,
        folder_id: null,
    }) as ImmutableMap<BackgroundAssetContainerModel>;
}

/*
TODO: IO-2579 -> The backend of asset generation should return an AssetContainer rather than a SlideBackgroundModel.
For now, we patch this on the frontend to maintain compatibility with the other media asset features.
*/
export type ModifiedAssetGenerationData = Omit<AssetGenerationData, 'asset'> & {
    asset: AssetContainerModel;
    isSaving?: boolean; // Track saving state per generation
};

export type ModifiedAssetGenerationResponse =
    | (Omit<AssetGenerationResponse, 'result'> & {
          result: ModifiedAssetGenerationData;
      })
    | null; // AssetGenerationResponse could be null in the case where the asset is still being generated

/**
 * @deprecated - IO-2579 - we want to merge the SlideBackgroundModel and AssetContainerModel types, so this should not be required soon
 */
export function convertAssetGenerationResponseToModifiedAssetGenerationResponse(
    assetGenerationResponse: AssetGenerationResponse[] | null[],
): ModifiedAssetGenerationResponse[] {
    return assetGenerationResponse.map((assetGenerationResponse, index) => {
        if (assetGenerationResponse?.result.asset) {
            return {
                ...assetGenerationResponse,
                result: {
                    ...assetGenerationResponse.result,
                    asset: ImmutableToJS(
                        convertImmutableSlideBackgroundToImmutableAssetContainer(
                            ImmutableFromJS(assetGenerationResponse.result.asset),
                        ),
                    ),
                    isSaving: false, // Initialize saving state
                },
            };
        }
        return assetGenerationResponse as unknown as ModifiedAssetGenerationResponse;
    });
}
/**
 * The code below is dynamically generating the search context types
 * and other related data for rendering the search tabs in the editor.
 * This is done to because DAM configs are only available at runtime.
 */

export type AssetCategory = {
    name: string;
    search_term: string;
};

const VISUAL_ASSET_CATEGORIES: AssetCategory[] = [
    {
        name: 'Nature',
        search_term: 'nature',
    },
    {
        name: 'People',
        search_term: 'people',
    },
    {
        name: 'City',
        search_term: 'city',
    },
    {
        name: 'Tech',
        search_term: 'tech',
    },
    {
        name: 'Abstract',
        search_term: 'abstract',
    },
];

const ICONS_ASSET_CATEGORIES: AssetCategory[] = [
    {
        name: 'Business',
        search_term: 'business',
    },
    {
        name: 'Education',
        search_term: 'education',
    },
    {
        name: 'Environment',
        search_term: 'environment',
    },
    {
        name: 'Health',
        search_term: 'health',
    },
    {
        name: 'Marketing',
        search_term: 'marketing',
    },
    {
        name: 'Social Media',
        search_term: 'social media',
    },
    {
        name: 'Technology',
        search_term: 'technology',
    },
];

const AUDIO_ASSET_CATEGORIES: AssetCategory[] = [
    {
        name: 'Angry',
        search_term: 'angry',
    },
    {
        name: 'Bright',
        search_term: 'bright',
    },
    {
        name: 'Calm',
        search_term: 'calm',
    },
    {
        name: 'Dark',
        search_term: 'dark',
    },
    {
        name: 'Dramatic',
        search_term: 'dramatic',
    },
    {
        name: 'Funky',
        search_term: 'funky',
    },
    {
        name: 'Happy',
        search_term: 'happy',
    },
    {
        name: 'Inspirational',
        search_term: 'inspirational',
    },
    {
        name: 'Romantic',
        search_term: 'romantic',
    },
    {
        name: 'Sad',
        search_term: 'sad',
    },
];

export const STICKER_ASSET_CATEGORIES: AssetCategory[] = [
    {
        name: 'Popular',
        search_term: 'popular',
    },
    {
        name: 'Arrow',
        search_term: 'arrow',
    },
    {
        name: 'Square',
        search_term: 'square shape outline',
    },
    {
        name: 'Circle',
        search_term: 'circle',
    },
    {
        name: 'Emphasis',
        search_term: 'emphasis',
    },
    {
        name: 'Subscribe CTA',
        search_term: 'subscribe',
    },
    {
        name: 'Follow CTA',
        search_term: 'follow',
    },
];

/**
 * filter by user level display names
 */
export enum FILTER_BY_LEVELS {
    filter_by_user_level = 'filter_by_user_level',
    filter_by_workspace_level = 'filter_by_workspace_level',
}

export type EditorAssetCollectionInformation = {
    search_source: MediaSearchSourceEnum | AudioSearchSourceEnum;
    no_query_search_source: MediaSearchSourceEnum | AudioSearchSourceEnum;
    description_text: string;
    empty_img_url: string;
    empty_message: string;
    attribution_text: string | null;
    button_text: string;
    button_icon: string | null;
    search_placeholder: string;
    SEARCH_CONTEXT: AssetCategory[];
    isVisualStockMedia?: boolean;
};

export type DAMSearchContextKeyType = `DAM_${string}_${number}`;
export type DAMSearchContextValueType = `dam-${string}-${number}`;

type STATIC_SEARCH_CONTEXT = {
    RECOMMENDED_VISUALS: 'recommended-visuals';
    UPLOADED_VISUALS: 'uploaded-visuals';
    TENOR_VISUALS: 'tenor-visuals';
    RECENT_VISUALS: 'recent-visuals';
    FAVORITE_VISUALS: 'favorite-visuals';
    STOCK_AUDIO: 'cc-audio';
    UPLOADED_AUDIO: 'uploaded-audio';
    RECENTS_AUDIO: 'recents-audio';
    FAVORITE_AUDIO: 'favorite-audio';
    RECOMMENDED_ICONS: 'recommended-icons';
    UPLOADED_ELEMENTS: 'uploaded-icons'; // Do not change value because its used for folder look up (see MediaFolder creation_source)
    UPLOADED_VOICEOVERS: 'uploaded-voiceovers';
    FAVORITE_AI_MEDIA: 'favorite-ai-media';
    AI_GENERATED_MEDIA: 'ai-generated-media';
    AI_GENERATED_CHARTS: 'ai-generated-charts';
    TENOR_STICKERS: 'tenor-stickers';
    WAVEFORMS: 'waveforms';
    TEXT_ELEMENTS: 'text-elements';
};

type DYNAMIC_SEARCH_CONTEXT = {
    [key: DAMSearchContextKeyType]: DAMSearchContextValueType;
};

export type SearchContextType = STATIC_SEARCH_CONTEXT & DYNAMIC_SEARCH_CONTEXT;
export type SearchContextKeyType = keyof SearchContextType;
export type SearchContextValueType = ValueOf<SearchContextType>;

export type UploadSearchContextType =
    | STATIC_SEARCH_CONTEXT['UPLOADED_VISUALS']
    | STATIC_SEARCH_CONTEXT['UPLOADED_ELEMENTS']
    | STATIC_SEARCH_CONTEXT['UPLOADED_AUDIO']
    | STATIC_SEARCH_CONTEXT['UPLOADED_VOICEOVERS'];

export function isUploadSearchContextType(value: unknown): value is UploadSearchContextType {
    return (
        value === SEARCH_CONTEXT.UPLOADED_VISUALS ||
        value === SEARCH_CONTEXT.UPLOADED_ELEMENTS ||
        value === SEARCH_CONTEXT.UPLOADED_AUDIO ||
        value === SEARCH_CONTEXT.UPLOADED_VOICEOVERS
    );
}

export type EditorTabSearchContextMapping = Record<
    EDITOR_TAB_OPTIONS,
    undefined | SearchContextValueType
>;
export type SearchContextEditorTabMapping = Record<SearchContextValueType, EDITOR_TAB_OPTIONS>;

export type EditorMediaTabContextsType = (typeof EDITOR_MEDIA_TAB_CONTEXTS)[number];
export type EditorMusicTabContextsType = (typeof EDITOR_MUSIC_TAB_CONTEXTS)[number];
export type EditorVoiceoverTabContextTypes = (typeof EDITOR_VOICEOVER_TAB_CONTEXTS)[number];
export type EditorDAMTabContextsType = (typeof EDITOR_DAM_TAB_CONTEXTS)[number];

export const createDAMSeachContextKey = (provider: string, id: number): DAMSearchContextKeyType =>
    `DAM_${provider.toUpperCase()}_${id}`;
export const createDAMSeachContextValue = (
    provider: string,
    id: number,
): DAMSearchContextValueType => `dam-${provider}-${id}`;

export function generateDynamicTypes() {
    const SEARCH_CONTEXT: SearchContextType = {
        RECOMMENDED_VISUALS: 'recommended-visuals' as const,
        UPLOADED_VISUALS: 'uploaded-visuals' as const,
        TENOR_VISUALS: 'tenor-visuals' as const,
        RECENT_VISUALS: 'recent-visuals' as const,
        FAVORITE_VISUALS: 'favorite-visuals' as const,
        STOCK_AUDIO: 'cc-audio' as const,
        UPLOADED_AUDIO: 'uploaded-audio' as const,
        RECENTS_AUDIO: 'recents-audio' as const,
        FAVORITE_AUDIO: 'favorite-audio' as const,
        RECOMMENDED_ICONS: 'recommended-icons' as const,
        UPLOADED_ELEMENTS: 'uploaded-icons' as const,
        UPLOADED_VOICEOVERS: 'uploaded-voiceovers' as const,
        FAVORITE_AI_MEDIA: 'favorite-ai-media' as const,
        AI_GENERATED_MEDIA: 'ai-generated-media' as const,
        AI_GENERATED_CHARTS: 'ai-generated-charts' as const,
        TENOR_STICKERS: 'tenor-stickers' as const,
        WAVEFORMS: 'waveforms' as const,
        TEXT_ELEMENTS: 'text-elements' as const,
    };

    const SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING: SearchContextEditorTabMapping = {
        [SEARCH_CONTEXT.RECOMMENDED_VISUALS]: EDITOR_TAB_OPTIONS.MEDIA,
        [SEARCH_CONTEXT.UPLOADED_VISUALS]: EDITOR_TAB_OPTIONS.MEDIA,
        [SEARCH_CONTEXT.TENOR_VISUALS]: EDITOR_TAB_OPTIONS.MEDIA,
        [SEARCH_CONTEXT.RECENT_VISUALS]: EDITOR_TAB_OPTIONS.MEDIA,
        [SEARCH_CONTEXT.FAVORITE_VISUALS]: EDITOR_TAB_OPTIONS.MEDIA,
        [SEARCH_CONTEXT.STOCK_AUDIO]: EDITOR_TAB_OPTIONS.MUSIC,
        [SEARCH_CONTEXT.UPLOADED_AUDIO]: EDITOR_TAB_OPTIONS.MUSIC,
        [SEARCH_CONTEXT.RECENTS_AUDIO]: EDITOR_TAB_OPTIONS.MUSIC,
        [SEARCH_CONTEXT.FAVORITE_AUDIO]: EDITOR_TAB_OPTIONS.MUSIC,
        [SEARCH_CONTEXT.RECOMMENDED_ICONS]: EDITOR_TAB_OPTIONS.ELEMENTS,
        [SEARCH_CONTEXT.UPLOADED_ELEMENTS]: EDITOR_TAB_OPTIONS.ELEMENTS,
        [SEARCH_CONTEXT.UPLOADED_VOICEOVERS]: EDITOR_TAB_OPTIONS.VOICEOVER,
        [SEARCH_CONTEXT.FAVORITE_AI_MEDIA]: EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA,
        [SEARCH_CONTEXT.AI_GENERATED_MEDIA]: EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA,
        [SEARCH_CONTEXT.AI_GENERATED_CHARTS]: EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA,
        [SEARCH_CONTEXT.TENOR_STICKERS]: EDITOR_TAB_OPTIONS.ELEMENTS,
        [SEARCH_CONTEXT.WAVEFORMS]: EDITOR_TAB_OPTIONS.ELEMENTS,
        [SEARCH_CONTEXT.TEXT_ELEMENTS]: EDITOR_TAB_OPTIONS.ELEMENTS,
    };

    const EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING: EditorTabSearchContextMapping = {
        [EDITOR_TAB_OPTIONS.MEDIA]: SEARCH_CONTEXT.RECOMMENDED_VISUALS,
        [EDITOR_TAB_OPTIONS.MUSIC]: SEARCH_CONTEXT.STOCK_AUDIO,
        [EDITOR_TAB_OPTIONS.ELEMENTS]: SEARCH_CONTEXT.TENOR_STICKERS,
        [EDITOR_TAB_OPTIONS.VOICEOVER]: SEARCH_CONTEXT.UPLOADED_VOICEOVERS,
        [EDITOR_TAB_OPTIONS.GENERATIVE_MEDIA]: SEARCH_CONTEXT.AI_GENERATED_MEDIA,
        [EDITOR_TAB_OPTIONS.DAM]: undefined,
        [EDITOR_TAB_OPTIONS.STYLE]: undefined,
        [EDITOR_TAB_OPTIONS.FORMAT]: undefined,
        [EDITOR_TAB_OPTIONS.ORIGINAL_CONTENT]: undefined,
        [EDITOR_TAB_OPTIONS.STAFF_CONTROLS]: undefined,
        [EDITOR_TAB_OPTIONS.CHAT]: undefined,
        [EDITOR_TAB_OPTIONS.NONE]: undefined,
    };

    const EDITOR_ASSET_COLLECTION_INFORMATION: Record<
        SearchContextValueType,
        EditorAssetCollectionInformation
    > = {
        [SEARCH_CONTEXT.RECOMMENDED_VISUALS]: {
            search_source: MediaSearchSourceEnum.RECOMMENDED,
            no_query_search_source: MediaSearchSourceEnum.CURATED,
            description_text: '',
            empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/media-empty.png', // shouldn't happen
            empty_message: 'No media found',
            attribution_text:
                'Powered by Shutterstock.\nShutterstock watermark will be removed when you download or share your video.',
            button_text: 'Library',
            button_icon: null,
            search_placeholder: 'Search millions of photos/videos...',
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
            isVisualStockMedia: true,
        },
        [SEARCH_CONTEXT.TENOR_VISUALS]: {
            search_source: MediaSearchSourceEnum.TENOR,
            no_query_search_source: MediaSearchSourceEnum.TENOR,
            description_text: "tenor's gif library",
            empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/media-empty.png', // shouldn't happen
            empty_message: 'No GIFs found',
            attribution_text: 'Powered by Tenor',
            button_text: 'GIFs',
            button_icon: null,
            search_placeholder: 'Search GIFs...',
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.UPLOADED_VISUALS]: {
            search_source: MediaSearchSourceEnum.UPLOADS,
            no_query_search_source: MediaSearchSourceEnum.UPLOADS,
            description_text: 'your uploads',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_11_UploadedMedia.png',
            empty_message: 'Your uploaded media\n will appear here',
            attribution_text: null,
            button_text: 'Uploads',
            button_icon: null,
            search_placeholder: 'Search your uploaded media...',
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.RECOMMENDED_ICONS]: {
            search_source: MediaSearchSourceEnum.ICON_LIBRARY,
            no_query_search_source: MediaSearchSourceEnum.ICON_LIBRARY,
            description_text: 'icons',
            empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/icons-icon.png', // shouldn't happen
            empty_message: 'No icons found',
            attribution_text: null,
            button_text: 'Icons',
            button_icon: null,
            search_placeholder: 'Search our collection of icons...',
            SEARCH_CONTEXT: ICONS_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.UPLOADED_ELEMENTS]: {
            search_source: MediaSearchSourceEnum.STICKERS_UPLOADS,
            no_query_search_source: MediaSearchSourceEnum.STICKERS_UPLOADS,
            description_text: 'your uploaded icons and stickers',
            empty_img_url: 'https://storage.googleapis.com/lumen5-site-images/icons-icon.png',
            empty_message: 'Your uploaded icons and stickers\n will appear here',
            attribution_text: null,
            button_text: 'Uploads',
            button_icon: null,
            search_placeholder: 'Search your uploaded icons and stickers...',
            SEARCH_CONTEXT: ICONS_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.RECENT_VISUALS]: {
            search_source: MediaSearchSourceEnum.RECENT,
            no_query_search_source: MediaSearchSourceEnum.RECENT,
            description_text: '',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_11_UploadedMedia.png',
            empty_message: 'Media used in published videos will appear here.',
            attribution_text: null,
            button_text: 'Recent',
            button_icon: null,
            search_placeholder: 'Search your recently used media...',
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
            isVisualStockMedia: true,
        },
        [SEARCH_CONTEXT.FAVORITE_VISUALS]: {
            search_source: MediaSearchSourceEnum.FAVORITES,
            no_query_search_source: MediaSearchSourceEnum.FAVORITES,
            description_text: '',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_9_FavoriteMedia.png',
            empty_message: 'Your favorited media will appear here',
            attribution_text: null,
            button_text: '',
            button_icon: 'star_border', // material-symbols-outlined classes only.
            search_placeholder: 'Search your favorited media...',
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
            isVisualStockMedia: true,
        },
        [SEARCH_CONTEXT.STOCK_AUDIO]: {
            search_source: AudioSearchSourceEnum.CC,
            no_query_search_source: AudioSearchSourceEnum.CC,
            description_text: 'free audio tracks',
            empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/music-empty.png', // shouldn't happen
            empty_message: 'No music found',
            attribution_text: null,
            button_text: 'Tracks',
            button_icon: null,
            search_placeholder: 'Search free audio tracks...',
            SEARCH_CONTEXT: AUDIO_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.UPLOADED_AUDIO]: {
            search_source: MediaSearchSourceEnum.UPLOADS,
            no_query_search_source: MediaSearchSourceEnum.UPLOADS,
            description_text: 'your uploaded audio',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_6_UploadedMusic.png',
            empty_message: 'Your uploaded music\n will appear here',
            attribution_text: null,
            button_text: 'Uploads',
            button_icon: null,
            search_placeholder: 'Search your uploaded audio...',
            SEARCH_CONTEXT: AUDIO_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.RECENTS_AUDIO]: {
            search_source: AudioSearchSourceEnum.RECENT,
            no_query_search_source: AudioSearchSourceEnum.RECENT,
            description_text: 'recently used tracks',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_7_PublishedTracks.png',
            empty_message: 'Tracks used in published\n videos will appear here',
            attribution_text: null,
            button_text: 'Recent',
            button_icon: null,
            search_placeholder: 'Search your recently used tracks...',
            SEARCH_CONTEXT: AUDIO_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.FAVORITE_AUDIO]: {
            search_source: AudioSearchSourceEnum.FAVORITES,
            no_query_search_source: AudioSearchSourceEnum.FAVORITES,
            description_text: 'favorite tracks',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_2_FavoriteTracks.png',
            empty_message: 'Your favorited tracks will appear here',
            attribution_text: null,
            button_text: '',
            button_icon: 'star_border', // material-symbols-outlined classes only.
            search_placeholder: 'Search your favorited tracks...',
            SEARCH_CONTEXT: AUDIO_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.UPLOADED_VOICEOVERS]: {
            search_source: AudioSearchSourceEnum.VOICEOVER_UPLOADS,
            no_query_search_source: AudioSearchSourceEnum.VOICEOVER_UPLOADS,
            description_text: 'your uploaded voiceover files',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_5_NoVO.png',
            empty_message: 'No voiceover files found',
            attribution_text: null,
            button_text: 'Uploads',
            search_placeholder: 'Search your uploaded voiceover...',
            button_icon: null,
            SEARCH_CONTEXT: AUDIO_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.FAVORITE_AI_MEDIA]: {
            search_source: MediaSearchSourceEnum.FAVORITE_AI_MEDIA,
            no_query_search_source: MediaSearchSourceEnum.FAVORITE_AI_MEDIA,
            description_text: '',
            empty_img_url:
                'https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/creator_panels/CI_9_FavoriteMedia.png',
            empty_message: 'Your saved AI media will appear here',
            attribution_text: null,
            button_text: '',
            button_icon: null,
            search_placeholder: '', // no ai media search for now
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.AI_GENERATED_MEDIA]: {
            search_source: MediaSearchSourceEnum.AI_GENERATED,
            no_query_search_source: MediaSearchSourceEnum.AI_GENERATED,
            description_text: '',
            empty_img_url: '',
            empty_message: '',
            attribution_text: null,
            button_text: '',
            button_icon: null,
            search_placeholder: '',
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.AI_GENERATED_CHARTS]: {
            search_source: MediaSearchSourceEnum.AI_GENERATED_CHARTS,
            no_query_search_source: MediaSearchSourceEnum.AI_GENERATED_CHARTS,
            description_text: '',
            empty_img_url: '',
            empty_message: 'No charts generated yet',
            attribution_text: null,
            button_text: '',
            button_icon: null,
            search_placeholder: '',
            SEARCH_CONTEXT: VISUAL_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.TENOR_STICKERS]: {
            search_source: MediaSearchSourceEnum.TENOR_STICKERS,
            no_query_search_source: MediaSearchSourceEnum.TENOR_STICKERS,
            description_text: "tenor's sticker library",
            empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/media-empty.png',
            empty_message: 'No stickers found',
            attribution_text: 'Powered by Tenor',
            button_text: 'Stickers',
            button_icon: null,
            search_placeholder: 'Search stickers...',
            SEARCH_CONTEXT: STICKER_ASSET_CATEGORIES,
        },
        [SEARCH_CONTEXT.WAVEFORMS]: {
            search_source: MediaSearchSourceEnum.WAVEFORMS,
            no_query_search_source: MediaSearchSourceEnum.WAVEFORMS,
            description_text: 'waveforms',
            empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/media-empty.png',
            empty_message: 'No waveforms found',
            attribution_text: '',
            button_text: 'Waveforms',
            button_icon: null,
            search_placeholder: 'Search waveforms...',
            SEARCH_CONTEXT: [],
        },
        [SEARCH_CONTEXT.TEXT_ELEMENTS]: {
            search_source: MediaSearchSourceEnum.UPLOADS,
            no_query_search_source: MediaSearchSourceEnum.UPLOADS,
            description_text: '',
            empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/media-empty.png',
            empty_message: '',
            attribution_text: '',
            button_text: 'Text',
            button_icon: null,
            search_placeholder: '',
            SEARCH_CONTEXT: [],
        },
    };

    const EDITOR_MEDIA_TAB_CONTEXTS = [
        // Order is important, it dictates the order of the tabs
        SEARCH_CONTEXT.RECOMMENDED_VISUALS,
        SEARCH_CONTEXT.TENOR_VISUALS,
        SEARCH_CONTEXT.UPLOADED_VISUALS,
        SEARCH_CONTEXT.RECENT_VISUALS,
        SEARCH_CONTEXT.FAVORITE_VISUALS,
    ];

    const EDITOR_MUSIC_TAB_CONTEXTS = [
        // Order is important, it dictates the order of the tabs
        SEARCH_CONTEXT.STOCK_AUDIO,
        SEARCH_CONTEXT.UPLOADED_AUDIO,
        SEARCH_CONTEXT.RECENTS_AUDIO,
        SEARCH_CONTEXT.FAVORITE_AUDIO,
    ];

    const EDITOR_ELEMENTS_TAB_CONTEXTS = [
        // Order is important, it dictates the order of the tabs
        SEARCH_CONTEXT.TENOR_STICKERS,
        SEARCH_CONTEXT.RECOMMENDED_ICONS,
        SEARCH_CONTEXT.WAVEFORMS,
        ...(Flags.canUseTextInElementsTab() ? [SEARCH_CONTEXT.TEXT_ELEMENTS] : []),
        SEARCH_CONTEXT.UPLOADED_ELEMENTS,
    ];

    const EDITOR_VOICEOVER_TAB_CONTEXTS = [
        // Order is important, it dictates the order of the tabs
        SEARCH_CONTEXT.UPLOADED_VOICEOVERS,
    ];

    const EDITOR_DAM_TAB_CONTEXTS: DAMSearchContextValueType[] = [
        // This is generated dynamically below
    ];

    const FILTER_BY_LEVEL_DISPLAY_NAMES: Record<
        SearchContextValueType,
        undefined | Record<FILTER_BY_LEVELS, string>
    > = {
        [SEARCH_CONTEXT.FAVORITE_VISUALS]: {
            [FILTER_BY_LEVELS.filter_by_workspace_level]: 'Starred by anyone',
            [FILTER_BY_LEVELS.filter_by_user_level]: 'Starred by me',
        },
        [SEARCH_CONTEXT.RECENT_VISUALS]: {
            [FILTER_BY_LEVELS.filter_by_workspace_level]: "Anyone's recents",
            [FILTER_BY_LEVELS.filter_by_user_level]: 'My recents',
        },
        [SEARCH_CONTEXT.RECENTS_AUDIO]: {
            [FILTER_BY_LEVELS.filter_by_workspace_level]: "Anyone's recents",
            [FILTER_BY_LEVELS.filter_by_user_level]: 'My recents',
        },
        [SEARCH_CONTEXT.FAVORITE_AUDIO]: {
            [FILTER_BY_LEVELS.filter_by_workspace_level]: 'Starred by anyone',
            [FILTER_BY_LEVELS.filter_by_user_level]: 'Starred by me',
        },
        [SEARCH_CONTEXT.FAVORITE_AI_MEDIA]: {
            [FILTER_BY_LEVELS.filter_by_workspace_level]: 'Starred by anyone',
            [FILTER_BY_LEVELS.filter_by_user_level]: 'Starred by me',
        },
        [SEARCH_CONTEXT.RECOMMENDED_VISUALS]: undefined,
        [SEARCH_CONTEXT.UPLOADED_VISUALS]: undefined,
        [SEARCH_CONTEXT.TENOR_VISUALS]: undefined,
        [SEARCH_CONTEXT.TENOR_STICKERS]: undefined,
        [SEARCH_CONTEXT.WAVEFORMS]: undefined,
        [SEARCH_CONTEXT.TEXT_ELEMENTS]: undefined,
        [SEARCH_CONTEXT.STOCK_AUDIO]: undefined,
        [SEARCH_CONTEXT.UPLOADED_AUDIO]: undefined,
        [SEARCH_CONTEXT.RECOMMENDED_ICONS]: undefined,
        [SEARCH_CONTEXT.UPLOADED_ELEMENTS]: undefined,
        [SEARCH_CONTEXT.UPLOADED_VOICEOVERS]: undefined,
        [SEARCH_CONTEXT.AI_GENERATED_MEDIA]: undefined,
        [SEARCH_CONTEXT.AI_GENERATED_CHARTS]: undefined,
    };

    const DAM_SEARCH_CONTEXT_CONFIG: Record<DAMSearchContextValueType, DAMConfig> = {};

    if (isUserLoggedIn()) {
        window.SERVER.USER.dam_configs.forEach((config) => {
            if (config.require_connection && !config.is_connected) {
                return;
            }
            const key: DAMSearchContextKeyType = createDAMSeachContextKey(
                config.provider,
                config.id,
            );
            const searchContext: DAMSearchContextValueType = createDAMSeachContextValue(
                config.provider,
                config.id,
            );

            SEARCH_CONTEXT[key] = searchContext;

            SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING[searchContext] = EDITOR_TAB_OPTIONS.DAM;
            const info: EditorAssetCollectionInformation = {
                search_source: MediaSearchSourceEnum.RECOMMENDED,
                no_query_search_source: MediaSearchSourceEnum.RECOMMENDED,
                description_text: '',
                empty_img_url: 'https://cdn.lumen5.com/lumen5-site-images/media-empty.png',
                empty_message: 'No media found',
                attribution_text: null,
                button_text: config.name,
                button_icon: null,
                search_placeholder: `Search ${config.name} library...`,
                SEARCH_CONTEXT: [],
            };

            EDITOR_ASSET_COLLECTION_INFORMATION[searchContext] = info;

            EDITOR_DAM_TAB_CONTEXTS.push(searchContext);

            DAM_SEARCH_CONTEXT_CONFIG[searchContext] = config;
        });

        EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING[EDITOR_TAB_OPTIONS.DAM] = EDITOR_DAM_TAB_CONTEXTS[0];
    }

    return {
        SEARCH_CONTEXT,
        SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING,
        EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING,
        EDITOR_ASSET_COLLECTION_INFORMATION,
        EDITOR_MEDIA_TAB_CONTEXTS,
        EDITOR_MUSIC_TAB_CONTEXTS,
        EDITOR_ELEMENTS_TAB_CONTEXTS,
        EDITOR_VOICEOVER_TAB_CONTEXTS,
        EDITOR_DAM_TAB_CONTEXTS,
        FILTER_BY_LEVEL_DISPLAY_NAMES,
        DAM_SEARCH_CONTEXT_CONFIG,
    };
}
export const {
    SEARCH_CONTEXT,
    SEARCH_CONTEXT_TO_EDITOR_TAB_MAPPING,
    EDITOR_TAB_TO_SEARCH_CONTEXT_MAPPING,
    EDITOR_ASSET_COLLECTION_INFORMATION,
    EDITOR_MEDIA_TAB_CONTEXTS,
    EDITOR_MUSIC_TAB_CONTEXTS,
    EDITOR_ELEMENTS_TAB_CONTEXTS,
    EDITOR_VOICEOVER_TAB_CONTEXTS,
    EDITOR_DAM_TAB_CONTEXTS,
    FILTER_BY_LEVEL_DISPLAY_NAMES,
    DAM_SEARCH_CONTEXT_CONFIG,
} = generateDynamicTypes();
